import { buildGatsbyAssetReference } from './asset'
import { buildGatsbyEntryReference } from './entryReference'
import { isRecord, isRichTextDocument, isRichTextField, UnknownRecord } from './shared'
import { ContentfulPreviewAsset, ContentfulPreviewEntry } from '../../services/contentfulPreviewService'

const EMBED_NODE_TYPES = new Set([
  'embedded-entry-block',
  'embedded-entry-inline',
  'embedded-asset-block',
  'entry-hyperlink',
  'asset-hyperlink',
])

/**
 * Live preview often sends embeds as resolved CPA entities (`sys.type: Entry|Asset` plus `fields`).
 * `gatsby-source-contentful`'s `renderRichText` resolves Links against build-time `references`
 * (which carry `__typename`). A resolved CPA target never matches those references, so
 * `BlogEmbedEntry` sees no `__typename` and returns null — the table vanishes until reload.
 *
 * Collapse every embed target back to a Link so resolution always uses the Gatsby-shaped reference.
 */
const toLinkTarget = (target: unknown): unknown => {
  if (!isRecord(target) || !isRecord(target.sys)) return target

  const id = typeof target.sys.id === 'string' ? target.sys.id : undefined
  if (!id) return target

  const linkType =
    target.sys.linkType === 'Asset' || target.sys.type === 'Asset'
      ? 'Asset'
      : target.sys.linkType === 'Entry' || target.sys.type === 'Entry'
      ? 'Entry'
      : undefined

  if (!linkType) return target

  return { sys: { type: 'Link', linkType, id } }
}

const normalizeRichTextEmbeds = (node: unknown): unknown => {
  if (Array.isArray(node)) return node.map(normalizeRichTextEmbeds)
  if (!isRecord(node)) return node

  const next: UnknownRecord = { ...node }

  if (Array.isArray(node.content)) {
    next.content = node.content.map(normalizeRichTextEmbeds)
  }

  if (typeof node.nodeType === 'string' && EMBED_NODE_TYPES.has(node.nodeType) && isRecord(node.data)) {
    next.data = { ...node.data, target: toLinkTarget(node.data.target) }
  }

  return next
}

/** Embed kinds whose target is an Asset, i.e. the ones that render an image. */
const ASSET_EMBED_NODE_TYPES = new Set(['embedded-asset-block', 'embedded-asset-inline'])

/** Embed kinds whose target is an Entry — Generic Table, OneBlock, OneLink on a blog post. */
const ENTRY_EMBED_NODE_TYPES = new Set(['embedded-entry-block', 'embedded-entry-inline', 'entry-hyperlink'])

const collectEmbedNodes = (node: unknown, embeds: UnknownRecord[] = []): UnknownRecord[] => {
  if (Array.isArray(node)) {
    node.forEach(child => collectEmbedNodes(child, embeds))
    return embeds
  }
  if (!isRecord(node)) return embeds

  if (typeof node.nodeType === 'string' && EMBED_NODE_TYPES.has(node.nodeType)) {
    embeds.push(node)
  }
  if (Array.isArray(node.content)) collectEmbedNodes(node.content, embeds)
  return embeds
}

const embedId = (node: UnknownRecord): string | undefined => {
  if (!isRecord(node.data) || !isRecord(node.data.target) || !isRecord(node.data.target.sys)) return undefined
  return typeof node.data.target.sys.id === 'string' ? node.data.target.sys.id : undefined
}

/**
 * If a live update drops an embed that still exists in the build-time document, put the build-time
 * embed node back so `renderRichText` can resolve it against `references`.
 */
const restoreMissingBuildEmbeds = (buildDoc: UnknownRecord, liveDoc: UnknownRecord): UnknownRecord => {
  if (!Array.isArray(buildDoc.content) || !Array.isArray(liveDoc.content)) return liveDoc

  const liveIds = new Set(
    collectEmbedNodes(liveDoc)
      .map(embedId)
      .filter((id): id is string => Boolean(id)),
  )

  const missing = collectEmbedNodes(buildDoc).filter(node => {
    const id = embedId(node)
    return id && !liveIds.has(id)
  })

  if (!missing.length) return liveDoc

  const content = [...liveDoc.content]
  for (const embed of missing) {
    const buildIndex = buildDoc.content.indexOf(embed)
    const insertAt = buildIndex === -1 ? content.length : Math.min(buildIndex, content.length)
    content.splice(insertAt, 0, normalizeRichTextEmbeds(embed) as UnknownRecord)
  }

  return { ...liveDoc, content }
}

const referenceFileUrl = (reference: unknown): string | undefined => {
  if (!isRecord(reference) || !isRecord(reference.file)) return undefined
  return typeof reference.file.url === 'string' ? reference.file.url : undefined
}

/**
 * Adds or refreshes the `references` entry for every Asset **and Entry** embed in the live document.
 *
 * `renderRichText` resolves the Links in `raw` against `references`, and `references` is build-time
 * data. So embedding an image that was not in the document at build time leaves a Link with nothing
 * to resolve against, and the embed renders as if it were not there — which is why a newly added
 * rich-text image did not appear until Refresh preview, even though the asset had been fetched and
 * its `gatsbyImageData` built. `overlayAsset` cannot help here: a rich-text embed is not a field on
 * an entry, so the overlay walk never reaches it.
 *
 * Re-uploaded files are handled the same way — the reference is replaced when its file url no
 * longer matches the asset we hold. Entry embeds (Generic Table, OneBlock, OneLink) are rebuilt by
 * `entryReference.ts`, which returns undefined for an unsupported type or an incomplete subtree, so
 * an embed we cannot build fully keeps its build-time data instead of rendering empty.
 */
const embeddedIdsOfKind = (document: UnknownRecord, nodeTypes: Set<string>): string[] =>
  collectEmbedNodes(document)
    .filter(node => typeof node.nodeType === 'string' && nodeTypes.has(node.nodeType))
    .map(embedId)
    .filter((id): id is string => Boolean(id))

const withLiveReferences = (
  value: UnknownRecord,
  document: UnknownRecord,
  entriesById: Map<string, ContentfulPreviewEntry>,
  assetsById: Map<string, ContentfulPreviewAsset>,
): unknown[] | undefined => {
  const assetIds = embeddedIdsOfKind(document, ASSET_EMBED_NODE_TYPES)
  const entryIds = embeddedIdsOfKind(document, ENTRY_EMBED_NODE_TYPES)

  if (!assetIds.length && !entryIds.length) return undefined

  const references = Array.isArray(value.references) ? [...value.references] : []
  const indexById = new Map<string, number>()
  references.forEach((reference, index) => {
    if (isRecord(reference) && typeof reference.contentful_id === 'string') {
      indexById.set(reference.contentful_id, index)
    }
  })

  let changed = false

  const upsert = (id: string, built: UnknownRecord | undefined, isEquivalent: (existing: unknown) => boolean) => {
    if (!built) return

    const existingIndex = indexById.get(id)

    if (existingIndex === undefined) {
      references.push(built)
      indexById.set(id, references.length - 1)
      changed = true
      return
    }

    if (!isEquivalent(references[existingIndex])) {
      references[existingIndex] = built
      changed = true
    }
  }

  for (const id of new Set(assetIds)) {
    const asset = assetsById.get(id)
    if (!asset) continue

    const built = buildGatsbyAssetReference(asset)
    // An asset reference only needs replacing when its file changed.
    upsert(id, built, existing => referenceFileUrl(existing) === referenceFileUrl(built))
  }

  for (const id of new Set(entryIds)) {
    const built = buildGatsbyEntryReference(id, entriesById, assetsById)
    // Entry references have no single identifying field, so compare the whole built shape. The
    // overlay walk already keeps *existing* embeds current by `contentful_id`; this is about embeds
    // that were not in the document at build time.
    upsert(id, built, existing => JSON.stringify(existing) === JSON.stringify(built))
  }

  return changed ? references : undefined
}

/**
 * Replaces the stringified document while keeping build-time `references`. Embed targets are
 * normalised to Links so Gatsby can resolve them; embeds dropped from a live update are restored
 * from the build-time document when possible.
 */
export const overlayRichText = (
  value: unknown,
  field: unknown,
  assetsById: Map<string, ContentfulPreviewAsset> = new Map(),
  entriesById: Map<string, ContentfulPreviewEntry> = new Map(),
): unknown | undefined => {
  if (!isRichTextDocument(field) || !isRichTextField(value)) return undefined

  let document = normalizeRichTextEmbeds(field) as UnknownRecord

  try {
    const buildDoc = JSON.parse(value.raw) as unknown
    if (isRichTextDocument(buildDoc)) {
      document = restoreMissingBuildEmbeds(buildDoc as UnknownRecord, document)
    }
  } catch {
    // Keep the normalised live document when build-time raw is not parseable.
  }

  const references = withLiveReferences(value, document, entriesById, assetsById)
  const next: UnknownRecord = { ...value, raw: JSON.stringify(document) }
  if (references) next.references = references

  return next
}
